import { CalendarCheck, CalendarClock, CalendarX } from 'lucide-react';
import type { NotificationBookingChange } from '@/types/Notification';

/**
 * How a booking touched by a call looks, shared by the notification bell and
 * the conversation list.
 *
 * Both show the same fact about the same call, and the database decides that
 * fact once — `user_notifications.sql` for the bell, `get_agents` for the
 * list, running the same `case`. Sharing the icon and the tone means the two
 * cannot drift visually either.
 *
 * Labels are not shared: the bell says "Moved to 14:30" and the list column
 * has room for "Moved", so each supplies its own key.
 */
export const BOOKING_CHANGE_STYLE: Record<
  NotificationBookingChange,
  { icon: typeof CalendarCheck; tone: string }
> = {
  created: {
    icon: CalendarCheck,
    tone: 'bg-foreground/[0.06] text-muted-foreground',
  },
  rescheduled: {
    icon: CalendarClock,
    tone: 'bg-foreground/[0.06] text-muted-foreground',
  },
  // A cancellation frees a slot the owner may want to refill, so it reads as
  // something that happened rather than as neutral metadata.
  cancelled: {
    icon: CalendarX,
    tone: 'bg-destructive/10 text-destructive',
  },
};

/** Short labels for the conversation list's Outcome column. */
export const BOOKING_CHANGE_LABEL: Record<
  NotificationBookingChange,
  { key: string; fallback: string }
> = {
  created: {
    key: 'agent.conversations.flags.booked.translation',
    fallback: 'Booked',
  },
  rescheduled: {
    key: 'agent.conversations.flags.moved.translation',
    fallback: 'Moved',
  },
  cancelled: {
    key: 'agent.conversations.flags.cancelled.translation',
    fallback: 'Cancelled',
  },
};
